import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { canonicalFor } from "../config/site";

/**
 * Consolidated FAQ page. Content is visible in the DOM (not collapsed
 * behind JS-only state) so AI crawlers and no-JS bots can read it, and it
 * emits FAQPage JSON-LD for rich results / AI citation.
 */

const FAQ_GROUPS = [
  {
    heading: "UAE — FTA / PINT AE",
    items: [
      {
        q: "Is Oracle Fusion natively compliant with the UAE FTA e-invoicing mandate?",
        a: "Not on its own. Oracle Fusion produces the source invoice data, but the UAE model requires exchange through an accredited service provider (ASP) on the PEPPOL-based network. Cloudare bridges Fusion to an ASP, typically using Oracle Integration Cloud (OIC) or a lightweight middleware layer, so the structured document is generated, validated and transmitted correctly.",
      },
      {
        q: "What is the difference between UAE PINT AE and KSA ZATCA?",
        a: "ZATCA (Saudi Arabia) uses a clearance/reporting model with cryptographic stamping and a national portal (Fatoora). The UAE follows a five-corner PEPPOL model (PINT AE) where accredited access points exchange documents between buyer and seller and report to the FTA. The data models, validation rules and onboarding steps differ, so an integration built for one is not portable to the other without rework.",
      },
      {
        q: "Do I need Oracle Integration Cloud (OIC) for UAE e-invoicing?",
        a: "Not strictly. OIC is a clean fit if you already run it, but Cloudare also delivers the connection through a focused middleware service (for example a Python/FastAPI integration layer) when OIC licensing or scope does not justify it. The right choice depends on how many source systems you have and your existing Oracle estate.",
      },
    ],
  },
  {
    heading: "Saudi Arabia — ZATCA",
    items: [
      {
        q: "What changes between ZATCA Phase 1 and Phase 2?",
        a: "Phase 1 (Generation, from December 2021) required businesses to issue structured electronic invoices with a QR code. Phase 2 (Integration, from January 2023, rolled out in waves) adds direct integration with ZATCA's Fatoora platform: UBL 2.1 XML, cryptographic stamping, UUID, invoice hash, and either real-time clearance for standard invoices or 24-hour reporting for simplified invoices.",
      },
      {
        q: "What is the wave system and which wave applies to my turnover?",
        a: "ZATCA notifies businesses of their Phase 2 integration date in waves, originally banded by annual VAT-subject revenue, moving progressively to smaller taxpayers. ZATCA gives at least six months' notice before a wave's go-live, so the practical answer is to be integration-ready ahead of your notified date rather than waiting for the letter.",
      },
      {
        q: "Can Cloudare integrate Oracle EBS with ZATCA without rebuilding it?",
        a: "Yes. We connect Oracle EBS to ZATCA through an integration layer that reads invoice data from EBS, generates the compliant XML, applies the stamp and QR, and handles clearance/reporting — without re-implementing EBS. This is a common pattern for organisations on older EBS releases that cannot move to Fusion before their ZATCA deadline.",
      },
    ],
  },
  {
    heading: "Malaysia — LHDN MyInvois",
    items: [
      {
        q: "What is the LHDN e-invoicing threshold and timeline?",
        a: "LHDN is rolling out MyInvois in phases banded by annual turnover, expanding toward all businesses over time. Because thresholds and dates have been adjusted during rollout, Cloudare confirms your applicable date against the latest LHDN guidance and prepares your integration ahead of it.",
      },
      {
        q: "How does a POS-to-LHDN integration work for hospitality and retail?",
        a: "High-volume B2C environments generate consolidated e-invoices rather than one cleared document per transaction. Cloudare connects the POS (for example Agilysys InfoGenesis in hospitality) to LHDN via a validation and submission layer, batching consumer transactions and handling the MyInvois acknowledgements without slowing the front desk or checkout.",
      },
    ],
  },
  {
    heading: "India — GST",
    items: [
      {
        q: "What is the difference between the e-invoice IRP and the e-way bill?",
        a: "The Invoice Registration Portal (IRP) validates a B2B invoice and returns an IRN and signed QR; it concerns the invoice itself. The e-way bill authorises the movement of goods above a value threshold and concerns transport. They are related but separate filings, and Cloudare automates both from your ERP where applicable.",
      },
      {
        q: "How do you handle 2A/2B reconciliation and vendor payment blocking on Oracle Fusion?",
        a: "Cloudare integrates GST return data (GSTR-2A/2B) with Fusion Payables so input-tax-credit mismatches are flagged before payment runs. Vendors whose invoices are not reflected in the portal can be held for review, which protects your ITC and reduces year-end reconciliation effort.",
      },
    ],
  },
  {
    heading: "Oracle Fusion / EBS / OIC",
    items: [
      {
        q: "APEX vs OIC vs VBCS — when should I pick which?",
        a: "Use OIC for system-to-system integration and orchestration across applications. Use APEX for data-centric internal tools and reporting layers over the database. Use VBCS for Oracle-native low-code UI extensions to Fusion. Cloudare often combines them — OIC for the e-invoicing pipeline, APEX or VBCS for the operational dashboards your finance team uses.",
      },
      {
        q: "When is Fusion BIP optimisation worth it?",
        a: "When BI Publisher reports are timing out, locking resources, or running long enough to affect period close. Optimising the data model, bursting, and SQL is worth it once report runtime or contention is a recurring operational cost rather than an occasional annoyance.",
      },
    ],
  },
  {
    heading: "Staffing",
    items: [
      {
        q: "C2C vs W2 vs permanent — which model fits a US engagement?",
        a: "W2 suits long-term roles where you want the worker on a single employer's payroll with benefits. C2C suits specialist or shorter engagements contracted through the consultant's own company. Permanent fits core team roles. Cloudare advises on the trade-offs (cost, compliance, flexibility) and supplies under whichever model fits the engagement.",
      },
      {
        q: "Are candidates pre-screened and background-verified?",
        a: "Yes. Candidates go through skills screening and reference checks before they reach your shortlist, and background verification is arranged according to the engagement and jurisdiction. For technical roles we assess hands-on capability, not just CV claims.",
      },
    ],
  },
  {
    heading: "Company",
    items: [
      {
        q: "Where is Cloudare headquartered, and where do you operate?",
        a: "Cloudare Technologies was established in 2017 with its registered presence in India and operating entities in India, the UAE and the United States. We serve clients across India, the Middle East, Malaysia, the UK and North America.",
      },
      {
        q: "How does a typical engagement start?",
        a: "We follow a structured path: discovery, solution architecture, commercial agreement, implementation, user acceptance testing, go-live, and knowledge transfer. The discovery and architecture stages are deliberately thorough so that scope, integration points and timelines are agreed before build work begins.",
      },
    ],
  },
];

function buildFaqJsonLd() {
  const mainEntity = [];
  FAQ_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      mainEntity.push({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      });
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

export default function FAQPage() {
  return (
    <React.Fragment>
      <Helmet>
        <title>Frequently Asked Questions | Cloudare</title>
        <meta
          name="description"
          content="Answers to common questions about ERP implementation and e-invoicing compliance — UAE PINT AE, KSA ZATCA, Malaysia LHDN, India GST, Oracle Fusion/EBS/OIC and staffing."
        />
        <link rel="canonical" href={canonicalFor("/faq")} />
        <script type="application/ld+json">
          {JSON.stringify(buildFaqJsonLd())}
        </script>
      </Helmet>

      <Navbar />
      <main
        style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: "48px 24px",
        }}
      >
        <h1 style={{ marginBottom: 8 }}>Frequently Asked Questions</h1>
        <p style={{ color: "#475569", marginBottom: 32 }}>
          Practical answers on ERP implementation and global e-invoicing
          compliance. For anything not covered here,{" "}
          <a href="/contact-us">get in touch</a>.
        </p>

        {FAQ_GROUPS.map((group) => (
          <section key={group.heading} style={{ marginBottom: 36 }}>
            <h2 style={{ borderBottom: "2px solid #eef", paddingBottom: 8 }}>
              {group.heading}
            </h2>
            {group.items.map((item, i) => (
              <article key={i} style={{ marginTop: 20 }}>
                <h3 style={{ marginBottom: 6, fontSize: "1.05rem" }}>
                  {item.q}
                </h3>
                <p style={{ color: "#334155", margin: 0 }}>{item.a}</p>
              </article>
            ))}
          </section>
        ))}
      </main>
      <Footer />
    </React.Fragment>
  );
}
